<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";

import { COMPONENT_API } from "@/config/social-api";
import { describeIcpEntrance, parseWeixinInt } from "@/utils/icp-entrance-status";

/** 此 errcode / info.status 下不可撤回备案（其余状态可尝试撤回） */
const ICP_WITHDRAW_BLOCKED_CODES = [26, 29, 1029];

/** info.status：未备案(1024)、驳回(3/5)；另见下方 1029 微信未备案 errcode */
const ICP_FORM_EDITABLE_INFO_STATUSES = [1024, 3, 5];

/**
 * 《小程序备案材料示例》互联网信息服务核准承诺书（个人/单位）：
 * 备案省为广东需自行上传模板；非广东由平台自动生成，无需本地上传。
 * @see https://developers.weixin.qq.com/minigame/product/record/record_material.html
 */
const ICP_COMMITMENT_MANUAL_PROVINCE_GUANGDONG = "440000";

/** apply_icp_filing：subject_other_materials / applets_other_materials 各最多 10 个 media_id */
const ICP_OTHER_MATERIALS_MAX = 10;

/** get_icp_entrance_info 未通过时 info.audit_data[]：key/key_name、error、suggest(可含 HTML) */
function normalizeIcpAuditData(info) {
  if (!info || !Array.isArray(info.audit_data)) {
    return [];
  }
  const rows = [];
  for (const row of info.audit_data) {
    if (!row || typeof row !== "object") continue;
    const keyName = (row.key_name || row.key || "").trim() || "审核项";
    const error = (row.error || "").trim();
    const suggest = row.suggest != null ? String(row.suggest) : "";
    if (!keyName && !error && !suggest) continue;
    rows.push({ keyName, error, suggest });
  }
  return rows;
}

function createDefaultIcpForm() {
  return {
    icp_subject: {
      base_info: {
        type: 4,
        name: "",
        province: "",
        city: "",
        district: "",
        address: "",
        comment: "",
      },
      organize_info: {
        certificate_type: 1,
        certificate_number: "",
        certificate_address: "",
        certificate_photo: "",
      },
      principal_info: {
        name: "",
        mobile: "",
        email: "",
        emergency_contact: "",
        certificate_type: "",
        certificate_number: "",
        certificate_validity_date_start: "",
        certificate_validity_date_end: "",
        certificate_photo_front: "",
        certificate_photo_back: "",
        authorization_letter: "",
      },
      legal_person_info: {
        name: "",
        certificate_number: "",
      },
    },
    icp_applets: {
      base_info: {
        service_content_types: [],
        nrlx_details: [{ type: "", code: "", media: "" }],
        comment: "",
      },
      principal_info: {
        name: "",
        mobile: "",
        email: "",
        emergency_contact: "",
        certificate_type: "",
        certificate_number: "",
        certificate_validity_date_start: "",
        certificate_validity_date_end: "",
        certificate_photo_front: "",
        certificate_photo_back: "",
        verify_task_id: "",
        authorization_letter: "",
      },
    },
    icp_materials: {
      commitment_letter: [""],
      subject_other_materials: [],
      applets_other_materials: [],
    },
  };
}

/**
 * form example
 * {
    "icp_subject": {
        "base_info": {
            "type": 4, // 企业类型
            "name": "李四王五有限责任公司",
            "province": "440000", // 广东省
            "city": "440100", // 广州市
            "district": "440105", // 海珠区
            "address": "新港中路TIT创意园B6栋",
            "comment": "主体备注"
        },
        "organize_info": {
            "certificate_type": 1, // 营业执照
            "certificate_number": "92111122223333443F",
            "certificate_address": "广东省广州市海珠区新港中路TIT创意园B6栋",
            "certificate_photo": "4ahCGpd3CYkE6RpkNkUR5czt3LvG8xDnDdKAz6bBKttSfM8p4k5Rj6823HXugPwQBurgMezyib7"
        },
        "principal_info": {
            "name": "李四",
            "mobile": "13022222221",
            "email": "lisi@lisiwangwucorp.com",
            "emergency_contact": "1722222234",
            "certificate_type": 2, // 身份证
            "certificate_number": "440105198802023334",
            "certificate_validity_date_start": "20230815",
            "certificate_validity_date_end": "20330815",
            "certificate_photo_front": "4ahCGpd3CYkE6RpkNkUR5czt3LvG8xDnDdKAz6bBKttSfM8p4k5Rj6823HXugPwQBurgMezyib7",
            "certificate_photo_back": "4ahCGpd3CYkE6RpkNkUR5czt3LvG8xDnDdKAz6bBKttSfM8p4k5Rj6823HXugPwQBurgMezyib7"
        }
    },
    "icp_applets": {
        "base_info": {
            "service_content_types": [
                3,
                4
            ], // 工具 & 家政
            "nrlx_details": [
                {
                    "type": 24 // 以上都不涉及
                }
            ],
            "comment": "小程序备注"
        },
        "principal_info": {
            "name": "王五",
            "mobile": "13011111111",
            "email": "wangwu@lisiwangwucorp.com",
            "emergency_contact": "15522223333",
            "certificate_type": 2, // 身份证
            "certificate_number": "440105199001019999",
            "certificate_validity_date_start": "20230815",
            "certificate_validity_date_end": "20330815",
            "certificate_photo_front": "4ahCGpd3CYkE6RpkNkUR5czt3LvG8xDnDdKAz6bBKttSfM8p4k5Rj6823HXugPwQBurgMezyib7",
            "certificate_photo_back": "4ahCGpd3CYkE6RpkNkUR5czt3LvG8xDnDdKAz6bBKttSfM8p4k5Rj6823HXugPwQBurgMezyib7",
            "verify_task_id": "R5PqRPNb6GmG3i0rqd4pTg"
        }
    },
    "icp_materials": {
        "commitment_letter": [
            "4ahCGpd3CYkE6RpkNkUR5czt3LvG8xDnDdKAz6bBKttSfM8p4k5Rj6823HXugPwQBurgMezyib7"
        ],
        "subject_other_materials": [],
        "applets_other_materials": []
    }
}
 */
/**
 * Starter page component
 */
export default {
  page() {
    return {
      title: this.title,
      meta: [{ name: "description" }],
    };
  },
  components: { Layout, PageHeader },
  data() {
    return {
      title: "Starter page",
      items: [
        {
          text: "Utility",
          href: "/",
        },
        {
          text: "Starter page",
          active: true,
        },
      ],
      loading: false,
      appid: "",
      userinfo: {},
      entranceLoading: false,
      entranceRaw: null,
      cancelIcpLoading: false,
      verifyLoading: false,
      queryVerifyLoading: false,
      form: createDefaultIcpForm(),
      provinceOptions: [], // 您提供的省市区数据
      cityOptions: [],
      districtOptions: [],
      enterpriseTypes: [],
      certificateTypes: [],
      contentTypes: [],
      nrlxDetails: [],
      imageUrl: {
        certificate_photo: "",
        certificate_photo_back: "",
        certificate_photo_front: "",
        authorization_letter: "",
        certificate_photo_back2: "",
        certificate_photo_front2: "",
        authorization_letter2: "",
        nrlx_details: "",
        commitment_letter: "",
      },
      subjectOtherPreviews: [],
      appletsOtherPreviews: [],
      legalset: "1",
      adminset: "0",
      activeNames: ["progress", "base", "admin", "app", "admin2", "materials"],
      isUploading: false,
    };
  },
  mounted() {
    try {
      this.userinfo =
        JSON.parse(localStorage.getItem("user") || "null") || {};
    } catch (e) {
      this.userinfo = {};
    }
    this.appid = this.$route.params.appid;
    this.title = "小程序备案";
    this.items = [
      { text: this.$t("menuitems.micoapp.text"), href: "/" },
      { text: this.$t("menuitems.icp.micoapp.list"), href: "/micoapp" },
      {
        text: this.$t("menuitems.icp.micoapp.info"),
        href: `/micoapp/${this.appid}`,
      },
      { text: "小程序备案", active: true },
    ];
    this.getinitdata();
    this.fetchEntrance();
  },
  methods: {
    cmpHeaders() {
      return this.userinfo && this.userinfo.openid
        ? { Openid: this.userinfo.openid }
        : {};
    },
    emptyIcpForm() {
      return JSON.parse(JSON.stringify(createDefaultIcpForm()));
    },
    deepMerge(a, b) {
      if (b === null || b === undefined) return a;
      if (Array.isArray(b)) return b.slice();
      if (typeof b !== "object") return b;
      if (!a || typeof a !== "object") return b;
      const out = { ...a };
      for (const k of Object.keys(b)) {
        const bv = b[k];
        if (bv !== null && typeof bv === "object" && !Array.isArray(bv)) {
          out[k] = this.deepMerge(a[k] || {}, bv);
        } else {
          out[k] = bv;
        }
      }
      return out;
    },
    normalizeIcpFormAfterLoad() {
      const nrlx = this.form.icp_applets.base_info.nrlx_details;
      if (!Array.isArray(nrlx) || nrlx.length === 0) {
        this.$set(this.form.icp_applets.base_info, "nrlx_details", [
          { type: "", code: "", media: "" },
        ]);
      } else if (!nrlx[0] || typeof nrlx[0] !== "object") {
        this.$set(this.form.icp_applets.base_info, "nrlx_details", [
          { type: "", code: "", media: "" },
        ]);
      }
      const cl = this.form.icp_materials.commitment_letter;
      if (typeof cl === "string" && cl) {
        this.form.icp_materials.commitment_letter = [cl];
      } else if (!Array.isArray(cl) || cl.length === 0) {
        this.form.icp_materials.commitment_letter = [""];
      }
      ["subject_other_materials", "applets_other_materials"].forEach((k) => {
        const raw = this.form.icp_materials[k];
        const arr = Array.isArray(raw)
          ? raw.map((x) => String(x || "").trim()).filter(Boolean)
          : [];
        this.$set(this.form.icp_materials, k, arr);
      });
      this.syncOtherMaterialsPreviewArrays();
    },
    syncOtherMaterialsPreviewArrays() {
      const sync = (previews, ids) => {
        const n = ids.length;
        while (previews.length < n) previews.push("");
        while (previews.length > n) previews.pop();
      };
      sync(this.subjectOtherPreviews, this.form.icp_materials.subject_other_materials);
      sync(this.appletsOtherPreviews, this.form.icp_materials.applets_other_materials);
    },
    /** 兼容嵌套 data；勿使用根字段 status（PHP 会写入 HTTP 状态 200） */
    normalizeEntrancePayload(raw) {
      if (!raw || typeof raw !== "object") return null;
      const inner = raw.data;
      if (
        inner &&
        typeof inner === "object" &&
        !Array.isArray(inner) &&
        (inner.errcode !== undefined || inner.info !== undefined)
      ) {
        return inner;
      }
      return raw;
    },
    async fetchEntrance() {
      this.entranceLoading = true;
      try {
        const res = await this.$axios.post(
          COMPONENT_API,
          { action: "geticpstatus", appid: this.appid },
          { headers: this.cmpHeaders() }
        );
        this.entranceRaw = this.normalizeEntrancePayload(res.data);
        const ec = parseWeixinInt(this.entranceRaw && this.entranceRaw.errcode);
        // 1029：未备案，属正常业务态，不弹错误提示
        if (
          this.entranceRaw &&
          ec !== null &&
          ec !== 0 &&
          ec !== 1029 &&
          this.entranceRaw.errmsg
        ) {
          this.$message.warning(this.entranceRaw.errmsg);
        }
      } catch (e) {
        console.error(e);
        this.entranceRaw = null;
      } finally {
        this.entranceLoading = false;
      }
    },
    async cancelIcpApply() {
      if (!this.icpWithdrawAllowed) {
        return;
      }
      try {
        await this.$confirm(
          "确定撤回当前备案申请吗？（需符合微信侧撤回条件）",
          "撤回备案",
          { type: "warning" }
        );
      } catch (e) {
        return;
      }
      this.cancelIcpLoading = true;
      try {
        const res = await this.$axios.post(
          COMPONENT_API,
          { action: "cancelicp", appid: this.appid },
          { headers: this.cmpHeaders() }
        );
        const d = res.data || {};
        if (Number(d.errcode) === 0) {
          this.$message.success("已请求撤回");
          await this.fetchEntrance();
        } else {
          this.$message.error(d.errmsg || "撤回失败");
        }
      } catch (e) {
        console.error(e);
        this.$message.error("请求失败");
      } finally {
        this.cancelIcpLoading = false;
      }
    },
    getBase64(file) {
      return new Promise(function (resolve, reject) {
        const reader = new FileReader();
        let imgResult = "";
        reader.readAsDataURL(file);
        reader.onload = function () {
          imgResult = reader.result;
        };
        reader.onerror = function (error) {
          reject(error);
        };
        reader.onloadend = function () {
          resolve(imgResult);
        };
      });
    },
    getinitdata() {
      var that = this;
      that.loading = true;
      that.$axios
        .post(
          COMPONENT_API,
          {
            action: "geticpinitdata",
            appid: that.appid,
          },
          { headers: that.cmpHeaders() }
        )
        .then(function (response) {
          that.loading = false;
          const d = response.data || {};
          that.contentTypes = d.servicetype || [];
          that.enterpriseTypes = d.subjecttypes || [];
          that.certificateTypes = d.certificatetypes || [];
          that.provinceOptions = d.localdata || [];
          that.nrlxDetails = d.nrlxDetails || [];
          if (
            d.info &&
            typeof d.info === "object" &&
            Object.keys(d.info).length > 0
          ) {
            that.form = that.deepMerge(that.emptyIcpForm(), d.info);
          }
          that.normalizeIcpFormAfterLoad();
        })
        .catch(function (error) {
          that.loading = false;
          console.log(error);
        });
    },
    certificate_photo_upload(file, fileList) {
      if (this.isUploading) {
        return;
      }

      this.isUploading = true;
      this.getBase64(file.raw).then((res) => {
        const params = res.split(",");
        if (params.length > 0) {
          this.imageUrl.certificate_photo = params[0] + "," + params[1];
          let field = "icp_subject.organize_info.certificate_photo";
          this.uploadimages(params[1], field)
            .then((media_id) => {
              this.form.icp_subject.organize_info.certificate_photo = media_id;
            })
            .catch((error) => {
              // 处理错误
              console.error(error);
            })
            .finally(() => {
              this.isUploading = false;
            });
        }
      });
    },
    certificate_photo_front_upload(file, fileList) {
      if (this.isUploading) {
        return;
      }

      this.isUploading = true;
      this.getBase64(file.raw).then((res) => {
        const params = res.split(",");
        if (params.length > 0) {
          this.imageUrl.certificate_photo_front = params[0] + "," + params[1];
          let field = "icp_subject.principal_info.certificate_photo_front";
          this.uploadimages(params[1], field)
            .then((media_id) => {
              this.form.icp_subject.principal_info.certificate_photo_front =
                media_id;
            })
            .catch((error) => {
              // 处理错误
              console.error(error);
            })
            .finally(() => {
              this.isUploading = false;
            });
        }
      });
    },
    certificate_photo_back_upload(file, fileList) {
      if (this.isUploading) {
        return;
      }

      this.isUploading = true;
      this.getBase64(file.raw).then((res) => {
        const params = res.split(",");
        if (params.length > 0) {
          this.imageUrl.certificate_photo_back = params[0] + "," + params[1];
          let field = "icp_subject.principal_info.certificate_photo_back";
          this.uploadimages(params[1], field)
            .then((media_id) => {
              this.form.icp_subject.principal_info.certificate_photo_back =
                media_id;
            })
            .catch((error) => {
              // 处理错误
              console.error(error);
            })
            .finally(() => {
              this.isUploading = false;
            });
        }
      });
    },
    authorization_letter_upload(file, fileList) {
      if (this.isUploading) {
        return;
      }

      this.isUploading = true;
      this.getBase64(file.raw).then((res) => {
        const params = res.split(",");
        if (params.length > 0) {
          this.imageUrl.authorization_letter = params[0] + "," + params[1];
          let field = "icp_subject.principal_info.authorization_letter";
          this.uploadimages(params[1], field)
            .then((media_id) => {
              this.form.icp_subject.principal_info.authorization_letter =
                media_id;
            })
            .catch((error) => {
              // 处理错误
              console.error(error);
            })
            .finally(() => {
              this.isUploading = false;
            });
        }
      });
    },
    certificate_photo_front2_upload(file, fileList) {
      if (this.isUploading) {
        return;
      }

      this.isUploading = true;
      this.getBase64(file.raw).then((res) => {
        const params = res.split(",");
        if (params.length > 0) {
          this.imageUrl.certificate_photo_front2 =
            params[0] + "," + params[1];
          let field = "icp_applets.principal_info.certificate_photo_front";
          this.uploadimages(params[1], field)
            .then((media_id) => {
              this.form.icp_applets.principal_info.certificate_photo_front =
                media_id;
            })
            .catch((error) => {
              // 处理错误
              console.error(error);
            })
            .finally(() => {
              this.isUploading = false;
            });
        }
      });
    },
    certificate_photo_back2_upload(file, fileList) {
      if (this.isUploading) {
        return;
      }

      this.isUploading = true;
      this.getBase64(file.raw).then((res) => {
        const params = res.split(",");
        if (params.length > 0) {
          this.imageUrl.certificate_photo_back2 =
            params[0] + "," + params[1];
          let field = "icp_applets.principal_info.certificate_photo_back";
          this.uploadimages(params[1], field)
            .then((media_id) => {
              this.form.icp_applets.principal_info.certificate_photo_back =
                media_id;
            })
            .catch((error) => {
              // 处理错误
              console.error(error);
            })
            .finally(() => {
              this.isUploading = false;
            });
        }
      });
    },
    authorization_letter2_upload(file, fileList) {
      if (this.isUploading) {
        return;
      }

      this.isUploading = true;
      this.getBase64(file.raw).then((res) => {
        const params = res.split(",");
        if (params.length > 0) {
          this.imageUrl.authorization_letter2 =
            params[0] + "," + params[1];
          let field = "icp_applets.principal_info.authorization_letter";
          this.uploadimages(params[1], field)
            .then((media_id) => {
              this.form.icp_applets.principal_info.authorization_letter =
                media_id;
            })
            .catch((error) => {
              // 处理错误
              console.error(error);
            })
            .finally(() => {
              this.isUploading = false;
            });
        }
      });
    },
    nrlx_details_upload(file, fileList) {
      if (this.isUploading) {
        return;
      }

      this.isUploading = true;
      this.getBase64(file.raw).then((res) => {
        const params = res.split(",");
        if (params.length > 0) {
          this.imageUrl.nrlx_details = params[0] + "," + params[1];
          let field = "icp_applets.base_info.nrlx_details.media";
          this.uploadimages(params[1], field)
            .then((media_id) => {
              if (!this.form.icp_applets.base_info.nrlx_details[0]) {
                this.$set(this.form.icp_applets.base_info.nrlx_details, 0, {
                  type: "",
                  code: "",
                  media: "",
                });
              }
              this.form.icp_applets.base_info.nrlx_details[0].media = media_id;
            })
            .catch((error) => {
              // 处理错误
              console.error(error);
            })
            .finally(() => {
              this.isUploading = false;
            });
        }
      });
    },
    commitment_letter_upload(file, fileList) {
      if (this.isUploading) {
        return;
      }
      this.isUploading = true;
      this.getBase64(file.raw).then((res) => {
        const params = res.split(",");
        if (params.length > 0) {
          this.imageUrl.commitment_letter = params[0] + "," + params[1];
          const field = "icp_materials.commitment_letter";
          this.uploadimages(params[1], field)
            .then((media_id) => {
              this.form.icp_materials.commitment_letter = [media_id];
            })
            .catch((error) => {
              console.error(error);
            })
            .finally(() => {
              this.isUploading = false;
            });
        }
      });
    },
    subject_other_material_upload(file) {
      if (this.isUploading) return;
      if (
        this.form.icp_materials.subject_other_materials.length >=
        ICP_OTHER_MATERIALS_MAX
      ) {
        this.$message.warning(
          `主体其他附件最多 ${ICP_OTHER_MATERIALS_MAX} 张`
        );
        return;
      }
      this.isUploading = true;
      this.getBase64(file.raw).then((res) => {
        const params = res.split(",");
        if (params.length < 2) {
          this.isUploading = false;
          return;
        }
        const preview = params[0] + "," + params[1];
        const field = "icp_materials.subject_other_materials";
        this.uploadimages(params[1], field)
          .then((media_id) => {
            this.form.icp_materials.subject_other_materials.push(media_id);
            this.subjectOtherPreviews.push(preview);
            this.$message.success("主体其他附件已上传");
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.isUploading = false;
          });
      });
    },
    applets_other_material_upload(file) {
      if (this.isUploading) return;
      if (
        this.form.icp_materials.applets_other_materials.length >=
        ICP_OTHER_MATERIALS_MAX
      ) {
        this.$message.warning(
          `小程序其他附件最多 ${ICP_OTHER_MATERIALS_MAX} 张`
        );
        return;
      }
      this.isUploading = true;
      this.getBase64(file.raw).then((res) => {
        const params = res.split(",");
        if (params.length < 2) {
          this.isUploading = false;
          return;
        }
        const preview = params[0] + "," + params[1];
        const field = "icp_materials.applets_other_materials";
        this.uploadimages(params[1], field)
          .then((media_id) => {
            this.form.icp_materials.applets_other_materials.push(media_id);
            this.appletsOtherPreviews.push(preview);
            this.$message.success("小程序其他附件已上传");
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.isUploading = false;
          });
      });
    },
    removeSubjectOtherMaterial(idx) {
      this.form.icp_materials.subject_other_materials.splice(idx, 1);
      this.subjectOtherPreviews.splice(idx, 1);
    },
    removeAppletsOtherMaterial(idx) {
      this.form.icp_materials.applets_other_materials.splice(idx, 1);
      this.appletsOtherPreviews.splice(idx, 1);
    },
    uploadimages(uri, field) {
      return new Promise((resolve, reject) => {
        var that = this;
        that.loading = true;
        that.$axios
          .post(
            COMPONENT_API,
            {
              action: "uploadimages",
              appid: that.appid,
              uri: uri,
              field: field,
              certificate: that.form.icp_subject.organize_info.certificate_type,
            },
            { headers: that.cmpHeaders() }
          )
          .then(function (response) {
            that.loading = false;
            resolve(response.data.media_id);
          })
          .catch(function (error) {
            console.log(error);
            that.loading = false;
            reject(error);
          });
      });
    },
    copyadmininfo() {
      this.form.icp_applets.principal_info =
        this.form.icp_subject.principal_info;
      this.imageUrl.certificate_photo_front2 =
        this.imageUrl.certificate_photo_front;
      this.imageUrl.certificate_photo_back2 =
        this.imageUrl.certificate_photo_back;
      this.imageUrl.authorization_letter2 = this.imageUrl.authorization_letter;
    },
    handleProvinceChange(value) {
      // 根据选择的省份更新城市选项
      const province = this.findItemByCode(this.provinceOptions, value);

      this.province = "";
      this.city = "";
      this.district = "";

      if (province) {
        this.cityOptions = province.sub_list;
        this.districtOptions = [];
        this.form.icp_subject.base_info.city = [];
        this.form.icp_subject.base_info.district = [];
      }
      if (!value) {
        this.districtOptions = [];
        this.form.icp_subject.base_info.city = [];
        this.form.icp_subject.base_info.district = [];
      }
    },
    handleCityChange(value) {
      // 根据选择的城市更新区县选项
      const city = this.findItemByCode(this.cityOptions, value);
      this.city = "";
      this.district = "";
      if (city) {
        this.districtOptions = city.sub_list;
        this.form.icp_subject.base_info.district = [];
      }
    },
    handleBaseTypeChange(value) {
      this.form.icp_subject.organize_info.certificate_type = "";
    },
    findItemByCode(list, code) {
      return list.find((item) => item.code === code);
    },
    make_principal_task() {
      var that = this;
      that.verifyLoading = true;
      that.$axios
        .post(
          COMPONENT_API,
          {
            action: "createIcpVerifyTask",
            appid: that.appid,
          },
          { headers: that.cmpHeaders() }
        )
        .then(function (response) {
          that.verifyLoading = false;
          const d = response.data || {};
          if (Number(d.errcode) === 0 && d.task_id) {
            that.form.icp_applets.principal_info.verify_task_id = d.task_id;
            that.$message({
              message: "已向管理员发送人脸核身通知",
              type: "success",
            });
          } else {
            that.$message({
              message: d.errmsg || d.msg || "发起失败",
              type: "error",
            });
          }
        })
        .catch(function (error) {
          that.verifyLoading = false;
          console.log(error);
        });
    },
    query_principal_task() {
      const tid = this.form.icp_applets.principal_info.verify_task_id;
      if (!tid) {
        this.$message.warning("请先发起人脸核身");
        return;
      }
      this.queryVerifyLoading = true;
      this.$axios
        .post(
          COMPONENT_API,
          {
            action: "queryIcpVerifyTask",
            appid: this.appid,
            taskid: tid,
          },
          { headers: this.cmpHeaders() }
        )
        .then((response) => {
          this.queryVerifyLoading = false;
          const d = response.data || {};
          if (Number(d.errcode) === 0) {
            this.$alert(JSON.stringify(d, null, 2), "人脸核身任务", {
              confirmButtonText: "关闭",
            });
          } else {
            this.$message.error(d.errmsg || "查询失败");
          }
        })
        .catch((error) => {
          this.queryVerifyLoading = false;
          console.log(error);
        });
    },
    submitForm() {
      var that = this;
      if (!that.icpFormEditable) {
        that.$message.warning(
          "当前备案状态下不可提交，请在未备案或审核驳回后再编辑提交"
        );
        return;
      }
      if (!that.form.icp_subject.base_info.type) {
        that.$message({ message: "请选择主体类型", type: "error" });
        return;
      }
      if (!that.form.icp_subject.organize_info.certificate_type) {
        that.$message({ message: "请选择证件类型", type: "error" });
        return;
      }
      if (
        !that.form.icp_subject.organize_info.certificate_photo &&
        that.form.icp_subject.base_info.type != 5
      ) {
        that.$message({ message: "请上传营业执照", type: "error" });
        return;
      }
      if (!that.form.icp_subject.base_info.name) {
        that.$message({ message: "请填写主体名称", type: "error" });
        return;
      }
      if (!that.form.icp_subject.organize_info.certificate_number) {
        that.$message({ message: "请填写证件号码", type: "error" });
        return;
      }
      if (!that.form.icp_subject.organize_info.certificate_address) {
        that.$message({ message: "请填写证件地址", type: "error" });
        return;
      }
      if (!that.form.icp_subject.base_info.province) {
        that.$message({ message: "请选择所在省份", type: "error" });
        return;
      }
      if (!that.form.icp_subject.base_info.city) {
        that.$message({ message: "请选择所在城市", type: "error" });
        return;
      }
      if (!that.form.icp_subject.base_info.district) {
        that.$message({ message: "请选择所在区县", type: "error" });
        return;
      }
      if (!that.form.icp_subject.base_info.address) {
        that.$message({ message: "请填写通讯地址", type: "error" });
        return;
      }
      if (!that.form.icp_subject.principal_info.name) {
        that.$message({ message: "请填写主体名称", type: "error" });
        return;
      }
      if (!that.form.icp_subject.principal_info.mobile) {
        that.$message({ message: "请填写手机号", type: "error" });
        return;
      }
      if (!that.form.icp_subject.principal_info.email) {
        that.$message({ message: "请填写邮箱", type: "error" });
        return;
      }
      if (!that.form.icp_subject.principal_info.emergency_contact) {
        that.$message({ message: "请填写紧急联系方式", type: "error" });
        return;
      }
      if (!that.form.icp_subject.principal_info.certificate_type) {
        that.$message({ message: "请选择证件类型", type: "error" });
        return;
      }
      if (!that.form.icp_subject.principal_info.certificate_number) {
        that.$message({ message: "请填写证件号码", type: "error" });
        return;
      }
      if (
        !that.form.icp_subject.principal_info.certificate_validity_date_start
      ) {
        that.$message({ message: "请选择证件有效期开始日期", type: "error" });
        return;
      }
      if (!that.form.icp_subject.principal_info.certificate_validity_date_end) {
        that.$message({ message: "请选择证件有效期结束日期", type: "error" });
        return;
      }
      if (!that.form.icp_subject.principal_info.certificate_photo_front) {
        that.$message({ message: "请上传证件照片正面", type: "error" });
        return;
      }
      if (!that.form.icp_subject.principal_info.certificate_photo_back) {
        that.$message({ message: "请上传证件照片背面", type: "error" });
        return;
      }
      if (
        !that.form.icp_subject.principal_info.authorization_letter &&
        that.legalset == 0
      ) {
        that.$message({ message: "请上传授权委托书", type: "error" });
        return;
      }
      if (
        !that.form.icp_applets.base_info.service_content_types ||
        !that.form.icp_applets.base_info.service_content_types.length
      ) {
        that.$message({ message: "请选择服务内容", type: "error" });
        return;
      }
      const nrlx0 = that.form.icp_applets.base_info.nrlx_details[0];
      if (
        !nrlx0 ||
        nrlx0.type === "" ||
        nrlx0.type === undefined ||
        nrlx0.type === null
      ) {
        that.$message({ message: "请选择前置审批类型", type: "error" });
        return;
      }
      if (!that.form.icp_applets.principal_info.name) {
        that.$message({ message: "请填写姓名", type: "error" });
        return;
      }
      if (!that.form.icp_applets.principal_info.mobile) {
        that.$message({ message: "请填写手机号", type: "error" });
        return;
      }
      if (!that.form.icp_applets.principal_info.email) {
        that.$message({ message: "请填写邮箱", type: "error" });
        return;
      }
      if (!that.form.icp_applets.principal_info.emergency_contact) {
        that.$message({ message: "请填写紧急联系方式", type: "error" });
        return;
      }
      if (!that.form.icp_applets.principal_info.certificate_type) {
        that.$message({ message: "请选择证件类型", type: "error" });
        return;
      }
      if (!that.form.icp_applets.principal_info.certificate_number) {
        that.$message({ message: "请填写证件号码", type: "error" });
        return;
      }
      if (
        !that.form.icp_applets.principal_info.certificate_validity_date_start
      ) {
        that.$message({ message: "请选择证件有效期开始日期", type: "error" });
        return;
      }
      if (!that.form.icp_applets.principal_info.certificate_validity_date_end) {
        that.$message({ message: "请选择证件有效期结束日期", type: "error" });
        return;
      }
      if (!that.form.icp_applets.principal_info.certificate_photo_front) {
        that.$message({ message: "请上传证件照片正面", type: "error" });
        return;
      }
      if (!that.form.icp_applets.principal_info.certificate_photo_back) {
        that.$message({ message: "请上传证件照片背面", type: "error" });
        return;
      }
      if (
        !that.form.icp_applets.principal_info.authorization_letter &&
        that.legalset == 0
      ) {
        that.$message({ message: "请上传授权委托书", type: "error" });
        return;
      }
      if (!that.form.icp_applets.principal_info.verify_task_id) {
        that.$message({
          message: "请完成小程序管理员人脸核身",
          type: "error",
        });
        return;
      }
      if (that.needsManualIcpCommitmentLetter) {
        const commitment = that.form.icp_materials.commitment_letter;
        if (
          !commitment ||
          !commitment.length ||
          !String(commitment[0] || "").trim()
        ) {
          that.$message({
            message:
              "备案省为广东时须上传《互联网信息服务核准承诺书》，请补传后再提交",
            type: "error",
          });
          return;
        }
      }
      if (
        that.form.icp_subject.principal_info.mobile ===
        that.form.icp_subject.principal_info.emergency_contact
      ) {
        that.$message({
          message: "紧急联系方式不能与手机号相同",
          type: "error",
        });
        return;
      }
      if (
        that.form.icp_subject.principal_info.name !=
        that.form.icp_subject.principal_info.name ||
        that.form.icp_subject.principal_info.name !=
        that.form.icp_applets.principal_info.name
      ) {
        that.$message({
          message: "个人主体管理员姓名必须与主体名称相同",
          type: "error",
        });
        return;
      }
      const formPayload = JSON.parse(JSON.stringify(that.form));
      if (!that.needsManualIcpCommitmentLetter) {
        formPayload.icp_materials.commitment_letter = [];
      }
      ["subject_other_materials", "applets_other_materials"].forEach(function (k) {
        const arr =
          formPayload.icp_materials && formPayload.icp_materials[k];
        if (Array.isArray(arr) && arr.length === 0) {
          delete formPayload.icp_materials[k];
        }
      });
      if (that.legalset) {
        delete formPayload.icp_subject.legal_person_info;
        delete formPayload.icp_applets.principal_info.authorization_letter;
        delete formPayload.icp_subject.principal_info.authorization_letter;
      }
      that.loading = true;

      that.$axios
        .post(
          COMPONENT_API,
          {
            action: "submiticpdata",
            appid: that.appid,
            form: formPayload,
          },
          { headers: that.cmpHeaders() }
        )
        .then(function (response) {
          that.loading = false;
          if (response.data.status == 200) {
            that.$message({
              message: "备案申请已提交，48小时内微信官方将对资料进行审核，请保证主体负责人与小程序负责人的手机号与紧急联系方式可以接通电话",
              type: "success",
              duration: 0,
              showClose: true,
            });
            that.$router.push({
              path: "/micoapp/" + that.appid,
            });
          } else {
            that.$message({
              message: response.data.msg,
              type: "error",
            });
          }
        })
        .catch(function (error) {
          that.loading = false;
          console.log(error);
        });
    },
  },
  computed: {
    /** 备案进度文案（不展示微信数字码，仅人读说明）；含 info.audit_data 未通过明细 */
    icpEntranceDisplay() {
      const r = this.entranceRaw;
      if (!r || typeof r !== "object") return null;

      let ec = parseWeixinInt(r.errcode);
      const info =
        r.info && typeof r.info === "object" && !Array.isArray(r.info)
          ? r.info
          : null;
      const st = parseWeixinInt(info ? info.status : null);

      if (ec === null && info) {
        ec = 0;
      }

      const auditItems = normalizeIcpAuditData(info);
      const rejectLegacy =
        info &&
        info.reject_reason &&
        String(info.reject_reason).trim()
          ? String(info.reject_reason).trim()
          : null;
      const common = { auditItems, reject: rejectLegacy };
      const described = describeIcpEntrance(r);
      const parts = [described.label];
      if (described.canceling) parts.push("正在注销备案");
      if (described.sms) parts.push("管局短信核验：" + described.sms);
      if (described.available) parts.push("备案入口" + described.available);

      if (ec === 1029 || st === 1029 || ec === 0) {
        return { ...common, progress: parts.filter(Boolean).join("；") };
      }

      if (ec !== null) {
        return {
          ...common,
          progress: r.errmsg || "查询备案进度失败",
        };
      }

      return { ...common, progress: "无法解析备案进度" };
    },
    /** 是否允许使用「撤回备案申请」（26 / 29 / 1029 除外；需已拉取过进度） */
    icpWithdrawAllowed() {
      const r = this.entranceRaw;
      if (!r || typeof r !== "object") return false;
      let ec = parseWeixinInt(r.errcode);
      const info =
        r.info && typeof r.info === "object" && !Array.isArray(r.info)
          ? r.info
          : null;
      const st = parseWeixinInt(info ? info.status : null);
      if (ec === null && info) {
        ec = 0;
      }
      if (ec !== null && ICP_WITHDRAW_BLOCKED_CODES.indexOf(ec) !== -1) {
        return false;
      }
      if (st !== null && ICP_WITHDRAW_BLOCKED_CODES.indexOf(st) !== -1) {
        return false;
      }
      return true;
    },
    hasIcpProvinceSelected() {
      const prov = this.form.icp_subject.base_info.province;
      return !(
        prov === undefined ||
        prov === null ||
        prov === "" ||
        prov === "0" ||
        prov === 0
      );
    },
    /**
     * 与微信备案材料示例一致：广东省需自行上传核准承诺书；非广东由平台生成，提交时空数组。
     */
    needsManualIcpCommitmentLetter() {
      if (!this.hasIcpProvinceSelected) return false;
      const prov = this.form.icp_subject.base_info.province;
      return String(prov) === ICP_COMMITMENT_MANUAL_PROVINCE_GUANGDONG;
    },
    icpOtherMaterialsMax() {
      return ICP_OTHER_MATERIALS_MAX;
    },
    filteredCertificateTypes() {
      return this.certificateTypes.filter((certificateTypeOption) => {
        return (
          certificateTypeOption.subject_type ===
          this.form.icp_subject.base_info.type
        );
      });
    },
    filteredCertificateTypesPes() {
      return this.certificateTypes.filter((certificateTypeOption) => {
        return certificateTypeOption.subject_type === 5;
      });
    },
    /** 未备案(1029)或 info.status∈1024/3/5（驳回）时可编辑，其余只读 */
    icpFormEditable() {
      const r = this.entranceRaw;
      if (!r || typeof r !== "object") return true;
      let ec = parseWeixinInt(r.errcode);
      const info =
        r.info && typeof r.info === "object" && !Array.isArray(r.info)
          ? r.info
          : null;
      const st = parseWeixinInt(info ? info.status : null);
      if (ec === null && info) {
        ec = 0;
      }
      if (ec === 1029 || st === 1029) {
        return true;
      }
      if (
        st !== null &&
        ICP_FORM_EDITABLE_INFO_STATUSES.indexOf(st) !== -1
      ) {
        return true;
      }
      return false;
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row micoapp-icp-form-page">
      <div class="col-12">
        <div class="card">
          <div class="card-body" v-loading="loading">
            <p class="text-muted small mb-3">
              请按下列板块完整填写；提交前须完成「小程序负责人」人脸核身。
              备案省为<strong>广东</strong>时须按
              <el-link
                type="primary"
                href="https://developers.weixin.qq.com/minigame/product/record/record_material.html"
                target="_blank"
                rel="noopener noreferrer"
              >《小程序备案材料示例》</el-link>
              上传《互联网信息服务核准承诺书》；其它省由微信平台自动生成该材料，无需在本页上传扫描件。
            </p>
            <el-form :model="form" ref="form" label-width="140px">
              <el-collapse v-model="activeNames">
                <el-collapse-item title="一、备案进度与撤回" name="progress">
                  <div v-loading="entranceLoading">
                    <el-button
                      type="primary"
                      size="small"
                      plain
                      class="mb-2"
                      @click="fetchEntrance"
                    >
                      刷新备案状态
                    </el-button>
                    <el-button
                      type="danger"
                      size="small"
                      plain
                      class="mb-2 ml-2"
                      :loading="cancelIcpLoading"
                      :disabled="!icpWithdrawAllowed"
                      @click="cancelIcpApply"
                    >
                      撤回备案申请
                    </el-button>
                    <template v-if="icpEntranceDisplay">
                      <dl class="icp-entrance-dl">
                        <dt>备案进度</dt>
                        <dd>{{ icpEntranceDisplay.progress }}</dd>
                        <template v-if="icpEntranceDisplay.auditItems.length">
                          <dt>未通过原因</dt>
                          <dd class="icp-audit-dd">
                            <div
                              v-for="(item, aidx) in icpEntranceDisplay.auditItems"
                              :key="'icp-audit-' + aidx"
                              class="icp-audit-item"
                            >
                              <div class="icp-audit-key">{{ item.keyName }}</div>
                              <div v-if="item.error" class="icp-audit-error">
                                {{ item.error }}
                              </div>
                              <div
                                v-if="item.suggest"
                                class="icp-audit-suggest"
                                v-html="item.suggest"
                              />
                            </div>
                          </dd>
                        </template>
                        <template v-else-if="icpEntranceDisplay.reject">
                          <dt>驳回原因</dt>
                          <dd>{{ icpEntranceDisplay.reject }}</dd>
                        </template>
                      </dl>
                    </template>
                    <p v-else-if="!entranceLoading" class="text-muted small mb-0">
                      点击「刷新备案状态」拉取微信侧最新进度。
                    </p>
                  </div>
                </el-collapse-item>
              </el-collapse>
              <div
                class="icp-form-body-wrap"
                :class="{ 'icp-form-body-readonly': !icpFormEditable }"
              >
                <el-collapse v-model="activeNames">
                <el-collapse-item title="二、主体信息" name="base">
                  <el-form-item label="主体类型">
                    <el-select v-model="form.icp_subject.base_info.type" @change="handleBaseTypeChange">
                      <el-option v-for="typeOption in enterpriseTypes" :key="typeOption.type" :label="typeOption.name"
                        :value="typeOption.type"></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item label="证件类型">
                    <el-select v-model="form.icp_subject.organize_info.certificate_type">
                      <el-option key="0" label="请选择" value="0"></el-option>
                      <el-option v-for="certificateTypeOption in filteredCertificateTypes"
                        :key="certificateTypeOption.type" :label="certificateTypeOption.name"
                        :value="certificateTypeOption.type"></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item label="营业执照" v-if="form.icp_subject.base_info.type != 5">
                    <el-upload class="avatar-uploader" action="#" accept=".jpg" :show-file-list="false"
                      :on-change="certificate_photo_upload">
                      <img v-if="imageUrl.certificate_photo" :src="imageUrl.certificate_photo" class="avatar" />
                      <i v-else class="el-icon-plus avatar-uploader-icon"></i>
                    </el-upload>
                  </el-form-item>
                  <el-form-item label="主体名称">
                    <el-input v-model="form.icp_subject.base_info.name"></el-input>
                  </el-form-item>
                  <el-form-item label="证件号码">
                    <el-input v-model="form.icp_subject.organize_info.certificate_number
                      "></el-input>
                  </el-form-item>
                  <el-form-item label="证件地址">
                    <el-input v-model="form.icp_subject.organize_info.certificate_address
                      "></el-input>
                  </el-form-item>
                  <el-form-item label="所在省份">
                    <el-select v-model="form.icp_subject.base_info.province" @change="handleProvinceChange">
                      <el-option key="0" label="请选择" value="0"></el-option>
                      <el-option v-for="provinceOption in provinceOptions" :key="provinceOption.code"
                        :label="provinceOption.name" :value="provinceOption.code"></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item label="所在城市">
                    <el-select v-model="form.icp_subject.base_info.city" @change="handleCityChange">
                      <el-option key="0" label="请选择" value="0"></el-option>
                      <el-option v-for="cityOption in cityOptions" :key="cityOption.code" :label="cityOption.name"
                        :value="cityOption.code"></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item label="所在区县">
                    <el-select v-model="form.icp_subject.base_info.district">
                      <el-option key="0" label="请选择" value="0"></el-option>
                      <el-option v-for="districtOption in districtOptions" :key="districtOption.code"
                        :label="districtOption.name" :value="districtOption.code"></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item label="通讯地址">
                    <el-input v-model="form.icp_subject.base_info.address"></el-input>
                  </el-form-item>
                  <el-form-item label="主体备注">
                    <el-input
                      v-model="form.icp_subject.base_info.comment"
                      type="textarea"
                      :rows="2"
                      placeholder="选填"
                    ></el-input>
                  </el-form-item>
                </el-collapse-item>
                <el-collapse-item title="三、主体负责人信息" name="admin">
                  <el-form-item label="负责人是否为法人" v-if="form.icp_subject.base_info.type != 5">
                    <el-radio v-model="legalset" label="1" val>法定代表人</el-radio>
                    <el-radio v-model="legalset" label="0">指定负责人</el-radio>
                  </el-form-item>
                  <el-form-item label="证件类型">
                    <el-select v-model="form.icp_subject.principal_info.certificate_type">
                      <el-option v-for="certificateTypeOption in filteredCertificateTypesPes"
                        :key="certificateTypeOption.type" :label="certificateTypeOption.name"
                        :value="certificateTypeOption.type"></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item label="姓名">
                    <el-input v-model="form.icp_subject.principal_info.name"></el-input>
                  </el-form-item>
                  <el-form-item label="手机号">
                    <el-input v-model="form.icp_subject.principal_info.mobile"></el-input>
                  </el-form-item>
                  <el-form-item label="邮箱">
                    <el-input v-model="form.icp_subject.principal_info.email"></el-input>
                  </el-form-item>
                  <el-form-item label="紧急联系方式">
                    <el-input v-model="form.icp_subject.principal_info.emergency_contact
                      "></el-input>
                  </el-form-item>
                  <el-form-item label="证件号码">
                    <el-input v-model="form.icp_subject.principal_info.certificate_number
                      "></el-input>
                  </el-form-item>
                  <el-form-item label="证件有效期">
                    <el-date-picker v-model="form.icp_subject.principal_info
                      .certificate_validity_date_start
                      " type="date" placeholder="开始日期" value-format="yyyyMMdd" style="width: 100%"></el-date-picker>
                    <el-date-picker v-model="form.icp_subject.principal_info
                      .certificate_validity_date_end
                      " type="date" placeholder="结束日期" value-format="yyyyMMdd" style="width: 100%"></el-date-picker>
                  </el-form-item>
                  <el-form-item label="身份证正面">
                    <el-upload class="avatar-uploader" action="#" accept=".jpg" :show-file-list="false"
                      :on-change="certificate_photo_front_upload">
                      <img v-if="imageUrl.certificate_photo_front" :src="imageUrl.certificate_photo_front"
                        class="avatar" />
                      <i v-else class="el-icon-plus avatar-uploader-icon"></i>
                    </el-upload>
                  </el-form-item>
                  <el-form-item label="身份证反面">
                    <el-upload class="avatar-uploader" action="#" accept=".jpg" :show-file-list="false"
                      :on-change="certificate_photo_back_upload">
                      <img v-if="imageUrl.certificate_photo_back" :src="imageUrl.certificate_photo_back" class="avatar" />
                      <i v-else class="el-icon-plus avatar-uploader-icon"></i>
                    </el-upload>
                  </el-form-item>
                  <el-form-item label="法人姓名" v-if="legalset == '0'">
                    <el-input v-model="form.icp_subject.legal_person_info.name"></el-input>
                  </el-form-item>
                  <el-form-item label="法人身份证号" v-if="legalset == '0'">
                    <el-input v-model="form.icp_subject.legal_person_info.certificate_number
                      "></el-input>
                  </el-form-item>

                  <el-form-item label="授权书" v-if="legalset == '0'">
                    <el-upload class="avatar-uploader" action="#" accept=".jpg" :show-file-list="false"
                      :on-change="authorization_letter_upload">
                      <img v-if="imageUrl.authorization_letter" :src="imageUrl.authorization_letter" class="avatar" />
                      <i v-else class="el-icon-plus avatar-uploader-icon"></i>
                    </el-upload>
                  </el-form-item>
                </el-collapse-item>
                <el-collapse-item title="四、小程序信息" name="app">
                  <el-form-item label="内容类型">
                    <el-select v-model="form.icp_applets.base_info.service_content_types" multiple filterable
                      :multiple-limit="5">
                      <el-option v-for="contentTypeOption in contentTypes" :key="contentTypeOption.type"
                        :label="contentTypeOption.name" :value="contentTypeOption.type"></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    v-if="form.icp_applets.base_info.nrlx_details[0]"
                    label="前置审批类型"
                  >
                    <el-select v-model="form.icp_applets.base_info.nrlx_details[0].type" placeholder="请选择">
                      <el-option v-for="nrlxDetailOption in nrlxDetails" :key="nrlxDetailOption.type"
                        :label="nrlxDetailOption.name" :value="nrlxDetailOption.type"></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item
                    label="前置审批号"
                    v-if="form.icp_applets.base_info.nrlx_details[0] &&
                      form.icp_applets.base_info.nrlx_details[0].type != null &&
                      form.icp_applets.base_info.nrlx_details[0].type !== '' &&
                      form.icp_applets.base_info.nrlx_details[0].type != 24
                    "
                  >
                    <el-input v-model="form.icp_applets.base_info.nrlx_details[0].code"></el-input>
                  </el-form-item>
                  <el-form-item
                    label="前置审批材料"
                    v-if="form.icp_applets.base_info.nrlx_details[0] &&
                      form.icp_applets.base_info.nrlx_details[0].type != null &&
                      form.icp_applets.base_info.nrlx_details[0].type !== '' &&
                      form.icp_applets.base_info.nrlx_details[0].type != 24
                    "
                  >
                    <el-upload class="avatar-uploader" action="#" accept=".jpg" :show-file-list="false"
                      :on-change="nrlx_details_upload">
                      <img v-if="imageUrl.nrlx_details" :src="imageUrl.nrlx_details" class="avatar" />
                      <i v-else class="el-icon-plus avatar-uploader-icon"></i>
                    </el-upload>
                  </el-form-item>
                  <el-form-item label="小程序描述">
                    <el-input v-model="form.icp_applets.base_info.comment" type="textarea" :rows="2"></el-input>
                  </el-form-item>
                </el-collapse-item>
                <el-collapse-item title="五、小程序负责人" name="admin2">
                  <el-form-item label="复用信息">
                    <el-button type="primary" @click="copyadmininfo">复用主体管理员信息</el-button>
                  </el-form-item>
                  <el-alert title="备案要求小程序管理员必须为备案中小程序负责人，如需更换请先对小程序管理员进行变更" type="warning">
                  </el-alert>
                  <el-form-item label="证件类型">
                    <el-select v-model="form.icp_applets.principal_info.certificate_type">
                      <el-option v-for="certificateTypeOption in filteredCertificateTypesPes"
                        :key="certificateTypeOption.type" :label="certificateTypeOption.name"
                        :value="certificateTypeOption.type"></el-option>
                    </el-select>
                  </el-form-item>
                  <el-form-item label="姓名">
                    <el-input v-model="form.icp_applets.principal_info.name"></el-input>
                  </el-form-item>
                  <el-form-item label="手机号">
                    <el-input v-model="form.icp_applets.principal_info.mobile"></el-input>
                  </el-form-item>
                  <el-form-item label="邮箱">
                    <el-input v-model="form.icp_applets.principal_info.email"></el-input>
                  </el-form-item>
                  <el-form-item label="紧急联系方式">
                    <el-input v-model="form.icp_applets.principal_info.emergency_contact
                      "></el-input>
                  </el-form-item>
                  <el-form-item label="证件号码">
                    <el-input v-model="form.icp_applets.principal_info.certificate_number
                      "></el-input>
                  </el-form-item>
                  <el-form-item label="证件有效期">
                    <el-date-picker v-model="form.icp_applets.principal_info
                      .certificate_validity_date_start
                      " type="date" placeholder="开始日期" value-format="yyyyMMdd" style="width: 100%"></el-date-picker>
                    <el-date-picker v-model="form.icp_applets.principal_info
                      .certificate_validity_date_end
                      " type="date" placeholder="结束日期" value-format="yyyyMMdd" style="width: 100%"></el-date-picker>
                  </el-form-item>
                  <el-form-item label="身份证正面">
                    <el-upload class="avatar-uploader" action="#" accept=".jpg" :show-file-list="false"
                      :on-change="certificate_photo_front2_upload">
                      <img v-if="imageUrl.certificate_photo_front2" :src="imageUrl.certificate_photo_front2"
                        class="avatar" />
                      <i v-else class="el-icon-plus avatar-uploader-icon"></i>
                    </el-upload>
                  </el-form-item>
                  <el-form-item label="身份证反面">
                    <el-upload class="avatar-uploader" action="#" accept=".jpg" :show-file-list="false"
                      :on-change="certificate_photo_back2_upload">
                      <img v-if="imageUrl.certificate_photo_back2" :src="imageUrl.certificate_photo_back2"
                        class="avatar" />
                      <i v-else class="el-icon-plus avatar-uploader-icon"></i>
                    </el-upload>
                  </el-form-item>
                  <el-form-item label="管理员人脸核身">
                    <el-button
                      type="primary"
                      v-if="!form.icp_applets.principal_info.verify_task_id"
                      size="small"
                      round
                      :loading="verifyLoading"
                      @click="make_principal_task"
                    >发起核身</el-button>
                    <template v-else>
                      <span class="small text-muted mr-2">task_id：{{ form.icp_applets.principal_info.verify_task_id }}</span>
                      <el-button type="default" size="small" round :loading="queryVerifyLoading" @click="query_principal_task">查询状态</el-button>
                    </template>
                  </el-form-item>
                  <el-form-item label="授权书" v-if="legalset == '0'">
                    <el-upload class="avatar-uploader" action="#" accept=".jpg" :show-file-list="false"
                      :on-change="authorization_letter2_upload">
                      <img v-if="imageUrl.authorization_letter2" :src="imageUrl.authorization_letter2" class="avatar" />
                      <i v-else class="el-icon-plus avatar-uploader-icon"></i>
                    </el-upload>
                  </el-form-item>
                </el-collapse-item>
                <el-collapse-item title="六、备案材料" name="materials">
                  <el-form-item
                    v-if="needsManualIcpCommitmentLetter"
                    label="备案承诺书"
                    required
                  >
                    <el-upload
                      class="avatar-uploader"
                      action="#"
                      accept=".jpg,.jpeg,.png"
                      :show-file-list="false"
                      :on-change="commitment_letter_upload"
                    >
                      <img
                        v-if="imageUrl.commitment_letter"
                        :src="imageUrl.commitment_letter"
                        class="avatar"
                      />
                      <i v-else class="el-icon-plus avatar-uploader-icon"></i>
                    </el-upload>
                    <p class="text-muted small mb-0 mt-2">
                      按微信材料示例准备并上传；图片将经服务端上传至微信备案材料接口。
                    </p>
                  </el-form-item>
                  <div v-else class="text-muted small">
                    <template v-if="!hasIcpProvinceSelected">
                      请先在「二、主体信息」中选择「所在省份」，系统将按
                      <a
                        href="https://developers.weixin.qq.com/minigame/product/record/record_material.html#_2-%E4%BA%92%E8%81%94%E7%BD%91%E4%BF%A1%E6%81%AF%E6%9C%8D%E5%8A%A1%E6%A0%B8%E5%87%86%E6%89%BF%E8%AF%BA%E4%B9%A6%EF%BC%88%E4%B8%AA%E4%BA%BA%EF%BC%89"
                        target="_blank"
                        rel="noopener noreferrer"
                      >微信材料示例</a>
                      判断是否需要上传《互联网信息服务核准承诺书》。
                    </template>
                    <template v-else>
                      当前「主体所在省份」非广东：依据微信
                      <a
                        href="https://developers.weixin.qq.com/minigame/product/record/record_material.html#_2-%E4%BA%92%E8%81%94%E7%BD%91%E4%BF%A1%E6%81%AF%E6%9C%8D%E5%8A%A1%E6%A0%B8%E5%87%86%E6%89%BF%E8%AF%BA%E4%B9%A6%EF%BC%88%E4%B8%AA%E4%BA%BA%EF%BC%89"
                        target="_blank"
                        rel="noopener noreferrer"
                      >《互联网信息服务核准承诺书（个人）》</a>
                      等说明，核准承诺书由微信平台自动生成，提交时无需在此上传扫描件。
                    </template>
                  </div>
                  <el-form-item label="主体其他附件">
                    <p class="text-muted small mb-2 icp-materials-hint">
                      温馨提示：根据规则提醒，上传授权书、承诺书、情况说明、辅助资质证明等主体其他附件，须图片清晰完整、不遮挡关键信息/图像、边角齐全、在有效期内，签字、单位盖章、填写内容与备案信息相符。若不涉及，直接跳过。
                    </p>
                    <div class="icp-other-materials-row">
                      <el-upload
                        class="avatar-uploader icp-other-uploader"
                        action="#"
                        accept=".jpg,.jpeg,.png"
                        :show-file-list="false"
                        :auto-upload="false"
                        :disabled="
                          form.icp_materials.subject_other_materials.length >=
                            icpOtherMaterialsMax || isUploading
                        "
                        :on-change="subject_other_material_upload"
                      >
                        <i
                          class="el-icon-plus avatar-uploader-icon"
                          title="添加图片"
                        />
                      </el-upload>
                      <div class="icp-other-thumbs">
                        <div
                          v-for="(mid, sidx) in form.icp_materials
                            .subject_other_materials"
                          :key="'som-' + sidx + '-' + mid"
                          class="icp-other-thumb-wrap"
                        >
                          <img
                            v-if="subjectOtherPreviews[sidx]"
                            :src="subjectOtherPreviews[sidx]"
                            class="icp-other-thumb"
                            alt=""
                          />
                          <div v-else class="icp-other-thumb-placeholder">
                            已传
                          </div>
                          <el-button
                            type="text"
                            class="icp-other-remove"
                            @click="removeSubjectOtherMaterial(sidx)"
                          >
                            移除
                          </el-button>
                        </div>
                      </div>
                    </div>
                    <p class="text-muted small mb-0">
                      可选，最多 {{ icpOtherMaterialsMax }} 张；与
                      <a
                        href="https://developers.weixin.qq.com/doc/oplatform/openApi/OpenApiDoc/miniprogram-management/record/applyIcpFiling.html"
                        target="_blank"
                        rel="noopener noreferrer"
                      >微信 apply_icp_filing</a>
                      字段 subject_other_materials 一致。
                    </p>
                  </el-form-item>
                  <el-form-item label="小程序其他附件">
                    <p class="text-muted small mb-2 icp-materials-hint">
                      温馨提示：根据规则提醒，上传授权书、承诺书、情况说明、辅助资质证明等小程序其他附件，须图片清晰完整、不遮挡关键信息/图像、边角齐全、在有效期内，签字、单位盖章、填写内容与备案信息相符。若不涉及，直接跳过。
                    </p>
                    <div class="icp-other-materials-row">
                      <el-upload
                        class="avatar-uploader icp-other-uploader"
                        action="#"
                        accept=".jpg,.jpeg,.png"
                        :show-file-list="false"
                        :auto-upload="false"
                        :disabled="
                          form.icp_materials.applets_other_materials.length >=
                            icpOtherMaterialsMax || isUploading
                        "
                        :on-change="applets_other_material_upload"
                      >
                        <i
                          class="el-icon-plus avatar-uploader-icon"
                          title="添加图片"
                        />
                      </el-upload>
                      <div class="icp-other-thumbs">
                        <div
                          v-for="(mid, aidx) in form.icp_materials
                            .applets_other_materials"
                          :key="'aom-' + aidx + '-' + mid"
                          class="icp-other-thumb-wrap"
                        >
                          <img
                            v-if="appletsOtherPreviews[aidx]"
                            :src="appletsOtherPreviews[aidx]"
                            class="icp-other-thumb"
                            alt=""
                          />
                          <div v-else class="icp-other-thumb-placeholder">
                            已传
                          </div>
                          <el-button
                            type="text"
                            class="icp-other-remove"
                            @click="removeAppletsOtherMaterial(aidx)"
                          >
                            移除
                          </el-button>
                        </div>
                      </div>
                    </div>
                    <p class="text-muted small mb-0">
                      可选，最多 {{ icpOtherMaterialsMax }} 张；字段 applets_other_materials。
                    </p>
                  </el-form-item>
                </el-collapse-item>
              </el-collapse>

              <el-form-item class="mt-4">
                <el-button
                  type="primary"
                  size="medium"
                  :disabled="!icpFormEditable"
                  @click="submitForm"
                >提交备案申请</el-button>
              </el-form-item>
              </div>
            </el-form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
<style>
.avatar-uploader .el-upload {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.avatar-uploader .el-upload:hover {
  border-color: #409eff;
}

.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 178px;
  height: 178px;
  line-height: 178px;
  text-align: center;
}

.avatar {
  width: 178px;
  height: 178px;
  display: block;
}

.icp-entrance-dl {
  margin: 0;
  font-size: 0.9rem;
}

.icp-entrance-dl dt {
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: 0.5rem;
}

.icp-entrance-dl dd {
  margin: 0;
  color: #1f2937;
}

.icp-materials-hint {
  line-height: 1.55;
}

.icp-other-materials-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 0.5rem;
}

.icp-other-uploader .avatar-uploader-icon {
  width: 88px;
  height: 88px;
  line-height: 88px;
}

.icp-other-thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.icp-other-thumb-wrap {
  text-align: center;
}

.icp-other-thumb {
  width: 88px;
  height: 88px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  display: block;
}

.icp-other-thumb-placeholder {
  width: 88px;
  height: 88px;
  line-height: 88px;
  font-size: 0.75rem;
  color: #9ca3af;
  background: #f9fafb;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
}

.icp-other-remove {
  display: block;
  margin: 4px auto 0;
  padding: 0;
  font-size: 12px;
}

.icp-audit-item {
  margin-top: 0.75rem;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.icp-audit-item:first-child {
  margin-top: 0.35rem;
}

.icp-audit-key {
  font-weight: 600;
  color: #991b1b;
  margin-bottom: 0.35rem;
}

.icp-audit-error {
  color: #b91c1c;
  font-size: 0.9rem;
  line-height: 1.45;
  margin-bottom: 0.35rem;
}

.icp-audit-suggest {
  font-size: 0.85rem;
  color: #4b5563;
  line-height: 1.5;
}

.icp-audit-suggest p {
  margin: 0.35em 0;
}

.icp-audit-suggest p:first-child {
  margin-top: 0;
}

.icp-audit-suggest p:last-child {
  margin-bottom: 0;
}

.icp-form-body-wrap.icp-form-body-readonly {
  pointer-events: none;
  opacity: 0.72;
}

</style>